import type { LocationType, Severity, ShipmentKind } from './common';
import type { Movement } from './events';
import { query } from './paging';
import type { ShipmentSummary } from './shipments';

export interface SeverityCounts { critical: number; warning: number; info: number }

/** KPI counts of one province (national view) or one regency (province view). */
export interface BreakdownRow {
  code: string;
  name: string;
  locations: number;
  gatewaysTotal: number;
  gatewaysOnline: number;
  cylindersTotal: number;
  cylindersActive: number;
  /** at a location, the owner warehouse included */
  atLocation: number;
  inTransit: number;
  missing: number;
  testDue: number;
  notSeen: number;
  alertsOpen: SeverityCounts;
}

/** A route of the last 7 days: cylinders moved from one location to another, with both ends' coordinates. */
export interface FlowRow {
  from: number;
  to: number;
  n: number;
  fromLat: number | null;
  fromLng: number | null;
  toLat: number | null;
  toLng: number | null;
}

/** The fields the overview reads of today's delivery notes (SHIPMENTS_TODAY_SQL). */
type OverviewShipmentField =
  | 'id' | 'number' | 'status' | 'isDemo' | 'vehiclePlate' | 'driverName' | 'originLocationId' | 'originName' | 'originType'
  | 'destinationLocationId' | 'destinationName' | 'destinationType' | 'createdAt' | 'departedAt' | 'receivedAt'
  | 'itemCount' | 'receivedCount' | 'missingCount' | 'extraCount' | 'scanning';
/** Absent from the overview's own rows (RE-CHECK against the final backend); a live message carries them. */
type OverviewShipmentOptional = 'unloadStartedAt' | 'cancelledAt' | 'updatedAt' | 'kind';
/**
 * A delivery note of today as the overview lists it (created, departed or received today, or still open): part
 * of a `ShipmentSummary`; a live `shipment.updated` message (the whole summary) also fits.
 */
export type OverviewShipment = Pick<ShipmentSummary, OverviewShipmentField> & Partial<Pick<ShipmentSummary, OverviewShipmentOptional>>;

/** A delivery note in transit, drawn on the map. */
export interface Truck {
  id: number;
  number: string | null;
  vehiclePlate: string;
  departedAt: string;
  isDemo: boolean;
  cylinders: number;
  originLocationId: number;
  originLat: number | null;
  originLng: number | null;
  destinationLocationId: number;
  destinationLat: number | null;
  destinationLng: number | null;
  /** RE-CHECK against the final backend: a withdrawal is drawn apart (absent = distribution) */
  kind?: ShipmentKind;
}

/** Cylinders of one delivery note that did not arrive (a sample of them). */
export interface MissingGroup {
  shipmentId: number;
  number: string | null;
  isDemo: boolean;
  count: number;
  cylinders: { id: number; serialNo: string; lastSeenAt: string | null; lastSeenLocation: string | null; seenSince: boolean | null }[];
}

/** An open "read without a delivery note" alert. */
export interface SeenWithoutShipment {
  alertId: number;
  createdAt: string;
  serialNo: string | null;
  cylinderId: number | null;
  seenLocation: string | null;
  recordedLocation: string | null;
}

/** First setup (nationally): the owner warehouse exists, a Reader is placed there, a cylinder registered, a Surat Jalan departed. */
export interface SetupState { warehouse: boolean; readerAtWarehouse: boolean; cylinderRegistered: boolean; shipmentDeparted: boolean; complete: boolean }

/** A needs-action alert as the Tindakan feed lists it (title and body rendered by the server, current names). */
export interface NeedsActionAlert {
  id: number;
  type: string;
  severity: Severity;
  title: string;
  body: string;
  createdAt: string;
  locationId: number | null;
  location: { id: number; name: string; regencyName: string | null } | null;
}

/** GET /overview: everything the landing page needs, nationally or for one province. */
export interface Overview {
  scope: { provinceCode: string | null; provinceName: string | null };
  generatedAt: string;
  /** the windows behind testDue and notSeen, in days */
  windows: { testDueDays: number; notSeenDays: number };
  kpi: {
    cylindersTotal: number;
    cylindersActive: number;
    locations: number;
    gatewaysTotal: number;
    gatewaysOnline: number;
    movementsToday: number;
    eventsToday: number;
    testDue: number;
    notSeen: number;
    unregisteredPresent: number;
    alertsOpen: SeverityCounts;
    /** Surat Jalan that departed / were received today (WIB), counted from rollups (newer backends) */
    shipmentsToday?: { departed: number; received: number };
    /** open alerts in Peringatan's "Perlu tindakan", by severity (newer backends; else alertsOpen) */
    needsAction?: SeverityCounts;
  };
  /** the four first-setup steps, decided by the server from data it has (newer backends) */
  setup?: SetupState;
  /** active cylinders along the chain */
  distribution: {
    atWarehouse: number;
    inTransit: number;
    /** recorded anywhere but the owner warehouse (= atFillingStations + atHubs + atTestWorkshops) */
    atLocations: number;
    locationsWithStock: number;
    /** recorded at each kind of place of the chain (newer backends) */
    atFillingStations?: number;
    atHubs?: number;
    /** full at the hubs: ready to go to households */
    hubStockFull?: number;
    atTestWorkshops?: number;
    /** with a household (Phase 3; 0 until the exchange exists) */
    withConsumer?: number;
    missing: number;
    shipmentsInTransit: number;
    /** simulated data exists: the demo banner shows */
    demo: boolean;
  };
  /** every province (national) or every regency of the province, zeros included, in code order */
  breakdown: { level: 'province' | 'regency'; items: BreakdownRow[] };
  movementsHourly: { hour: string; n: number }[];
  recentMovements: Movement[];
  flows7d: FlowRow[];
  shipmentsToday: OverviewShipment[];
  trucks: Truck[];
  needsAction: { missing: MissingGroup[]; seenWithoutShipment: SeenWithoutShipment[]; testDue: number; /** newest needs-action alerts, critical first (newer backends) */ alerts?: NeedsActionAlert[] };
}

/** One WIB day of GET /overview/trend (zeros included). */
export interface TrendDay {
  /** YYYY-MM-DD, WIB */
  day: string;
  /** Surat Jalan departed */
  departed: number;
  /** Surat Jalan received (complete or with a discrepancy) */
  received: number;
  /** received with a discrepancy */
  discrepancy: number;
  /** cylinders read */
  reads: number;
  /** cylinders registered */
  registered: number;
}
/** Oldest first, always `days` entries. */
export interface Trend { provinceCode: string | null; days: TrendDay[] }

/** [south, west, north, east] in degrees (the dashboard's order; the API sends `{ west, south, east, north }`). */
export type MapBounds = [number, number, number, number];
/** The API's bounds: degrees, edges included. */
export interface ApiBounds { west: number; south: number; east: number; north: number }
/** What GET /overview/map aggregates by: per province (national zoom), per regency (needs a province), single Titik Distribusi. */
export type MapLevel = 'province' | 'regency' | 'points';

export interface ReaderCounts { online: number; total: number }

/** One province or regency: one calm bubble at `center` (the mean of its located Titik Distribusi). */
export interface MapUnit {
  code: string;
  name: string;
  center: { lat: number; lng: number } | null;
  bounds: ApiBounds | null;
  /** Titik Distribusi (the owner warehouse included) */
  locations: number;
  /** active cylinders present at its places */
  cylinders: number;
  readers: ReaderCounts;
  needsAction: SeverityCounts;
  /** delivery notes in transit leaving it / heading to it */
  trucks: { outbound: number; inbound: number };
  /** open orders past their SLA at its Hubs; orders out for delivery (Phase 4; null at village level for a caller
   * without pii.read) */
  slaBreached?: number | null;
  deliveriesOut?: number | null;
}
/** One Titik Distribusi close in. */
export interface MapSpot {
  id: number;
  code: string;
  name: string;
  type: LocationType;
  lat: number;
  lng: number;
  regencyCode: string;
  cylinders: number;
  readers: ReaderCounts;
  needsAction: SeverityCounts;
  slaBreached?: number | null;
  deliveriesOut?: number | null;
}
/** Several Titik Distribusi of one grid cell, when an area holds more than the server's cap. */
export interface MapCluster {
  lat: number;
  lng: number;
  bounds: ApiBounds;
  count: number;
  cylinders: number;
  readers: ReaderCounts;
  needsAction: SeverityCounts;
}
/** A place a truck travels between. */
export interface MapTripEnd { id: number; name: string; lat: number; lng: number }
/**
 * A delivery note in transit, estimated (there is no GPS): where it would be if it drives at the route's usual pace.
 * Between polls the dashboard moves it on from `departedAt` towards `expectedArrivalAt`. The driver is masked by the
 * server for a role without `pii.read`.
 */
export interface MapTrip {
  shipmentId: number;
  number: string | null;
  isDemo: boolean;
  origin: MapTripEnd;
  destination: MapTripEnd;
  departedAt: string;
  expectedArrivalAt: string;
  etaBasis: 'route_history' | 'distance';
  progress: number;
  estimatedPosition: { lat: number; lng: number };
  overdue: boolean;
  cylinderCount: number;
  vehiclePlate: string;
  driverName: string;
  /** a photo of the vehicle, once a vehicle registry exists (absent today: the card shows the illustration) */
  vehiclePhotoUrl?: string | null;
  /** a withdrawal (Penarikan) is drawn apart; absent from older backends = distribution */
  kind?: ShipmentKind;
}
/** Trucks in transit between two units of the answer (by code), busiest first. */
export interface TransitFlow { from: string; to: string; n: number }

/** GET /overview/map: the caller's scope at one level, bounded (clusters past the cap, `capped` trucks). */
export interface OverviewMap {
  level: MapLevel;
  scope: { provinceCode: string | null; regencyCode: string | null; networkLocationId: number | null };
  asOf: string;
  /** what the answer covers: the units' bounds, or the snapped bbox close in */
  bounds: ApiBounds | null;
  units: MapUnit[];
  points: MapSpot[];
  clusters: MapCluster[];
  trucks: {
    mode: 'aggregate' | 'individual';
    total: number;
    capped: boolean;
    flows: TransitFlow[];
    items: MapTrip[];
  };
}
export interface OverviewMapQuery { level: MapLevel; bbox?: MapBounds | null; provinceCode?: string; regencyCode?: string }

/** A Hub with couriers on the road (GET /overview/deliveries): its position is a Titik Distribusi's. */
export interface DeliveryHub { hubId: number; hubName: string; lat: number; lng: number; n: number; overdue: number }
/**
 * An order out for delivery, Hub -> the household's VILLAGE: a point the server moves 300-1,500 m from the village's
 * middle by the order id, never the house. No names, phones, addresses or courier ids.
 */
export interface DeliveryOut {
  orderId: number;
  number: string;
  hub: { id: number; name: string; lat: number; lng: number };
  village: { code: string; name: string; lat: number; lng: number };
  departedAt: string;
  expectedAt: string;
  /** the order's SLA has passed */
  overdue: boolean;
}
/** GET /overview/deliveries: couriers on a motorbike, individually up to the server's cap, per Hub always. */
export interface OverviewDeliveries { mode: 'aggregate' | 'individual'; total: number; capped: boolean; asOf: string; hubs: DeliveryHub[]; items: DeliveryOut[] }

/** GET /overview/chain: the flow strip Gudang -> SPBG -> Hub -> Warga (+ Bengkel Uji, Dimusnahkan). */
export interface OverviewChain {
  scope: { provinceCode: string | null; networkLocationId: number | null };
  asOf: string;
  /** active cylinders now; destroyed: all time */
  /** households: null for a caller without pii.read (small-number suppression) */
  nodes: { warehouse: number; fillingStations: number; hubs: number; households: number | null; couriers: number; testWorkshops: number; inTransit: number; missing: number; destroyed: number };
  /** WIB today */
  today: { warehouseToFilling: number; fillingToHub: number; hubToFilling: number; toTestWorkshop: number; fromTestWorkshop: number; hubToHouseholds: number | null; destroyed: number };
  trucks: { DISTRIBUTION: number; WITHDRAWAL: number };
  deliveries: { out: number; overdue: number };
}

/** Bounds as the query string carries them: west,south,east,north (rounded: nearby views share a cache entry). */
const bboxParam = (b: MapBounds | null | undefined) => (b ? [b[1], b[0], b[3], b[2]].map((x) => x.toFixed(3)).join(',') : undefined);

export const overviewPaths = {
  /** national without a province */
  overview: (provinceCode: string) => `/overview${query({ provinceCode })}`,
  trend: (days: number, provinceCode: string) => `/overview/trend${query({ days, provinceCode })}`,
  map: (q: OverviewMapQuery) => `/overview/map${query({ level: q.level, bbox: bboxParam(q.bbox), provinceCode: q.provinceCode, regencyCode: q.regencyCode })}`,
  /** the same query as the map's */
  deliveries: (q: OverviewMapQuery) => `/overview/deliveries${query({ level: q.level, bbox: bboxParam(q.bbox), provinceCode: q.provinceCode, regencyCode: q.regencyCode })}`,
  chain: (provinceCode: string) => `/overview/chain${query({ provinceCode })}`,
};
